'use client';

import { useEffect, useRef, useState } from 'react';

/**
 * Looping card animation, gated on FULL PAGE LOAD, then viewport visibility.
 *
 * The sequencing is the whole design: photos (the LCP image) and every other
 * critical asset finish first — video bytes are only requested after the
 * window `load` event, so field LCP/FCP never compete with card videos.
 * After load, an IntersectionObserver drives playback by visibility: cards
 * in or near the viewport animate immediately, off-screen cards never
 * download, and scrolled-away cards pause (battery/CPU) while keeping their
 * buffer (sticky src — no re-fetch when scrolled back).
 *
 * Client-side navigations see `document.readyState === 'complete'`, so
 * subsequent pages animate instantly — only the initial hard load defers.
 * Repeat visits mostly replay from cache (year-long cache-control on the
 * video bucket).
 *
 * The video fades in once it's actually playing, so there's never a black
 * flash or layout shift.
 */
export function CardVideo({
  src,
  className = '',
}: {
  src: string;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  // `active` loads the src (sticky — once fetched it stays attached).
  const [active, setActive] = useState(false);
  const visibleRef = useRef(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let observer: IntersectionObserver | undefined;
    let cancelled = false;

    const arm = () => {
      if (cancelled) return;
      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            visibleRef.current = entry.isIntersecting;
            if (entry.isIntersecting) {
              setActive(true);
              el.play()?.catch(() => {});
            } else {
              el.pause();
            }
          }
        },
        // Start fetching a bit before the card scrolls into view so the
        // animation is already running when it appears.
        { rootMargin: '25% 0px' },
      );
      observer.observe(el);
    };

    if (document.readyState === 'complete') {
      arm();
    } else {
      window.addEventListener('load', arm, { once: true });
    }

    return () => {
      cancelled = true;
      window.removeEventListener('load', arm);
      observer?.disconnect();
    };
  }, []);

  // Critical: play() must run AFTER the src is attached to the element. The
  // observer flips `active`, React re-renders with the src, and only then
  // does this effect fire and start playback — plus a `loadeddata` retry for
  // browsers that need the buffer ready first. (Doing play() inside the
  // handler alone ran before the src existed, so it silently no-op'd and
  // the card stayed a static poster.)
  useEffect(() => {
    const el = ref.current;
    if (!el || !active) return;

    const tryPlay = () => {
      if (visibleRef.current) el.play()?.catch(() => {});
    };
    tryPlay();
    el.addEventListener('loadeddata', tryPlay);
    el.addEventListener('canplay', tryPlay);
    return () => {
      el.removeEventListener('loadeddata', tryPlay);
      el.removeEventListener('canplay', tryPlay);
    };
  }, [active]);

  return (
    <video
      ref={ref}
      src={active ? src : undefined}
      muted
      loop
      playsInline
      preload="none"
      aria-hidden="true"
      onPlaying={() => setPlaying(true)}
      onPause={() => setPlaying(false)}
      className={`${className} transition-opacity duration-500 ${
        playing ? 'opacity-100' : 'opacity-0'
      }`}
    />
  );
}
